import { invoiceLanguageToLocale } from '@features/invoices/constants/invoiceLanguageToLocale'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { Invoice } from '@features/invoices/types/invoices/invoice'
import { Quote } from '@features/invoices/types/quotes/quote'
import { getDisplayedPaymentMethods } from '@features/invoices/utils/getDisplayedPaymentMethods'
import { sanitizeStringField } from '@features/invoices/utils/sanitizeStringField'
import type { GetFilteredVatRates } from '@features/vatRates/hooks/useFilteredVatRates'
import type { VatRate } from '@features/vatRates/types/vatRate'

import { NumberValue } from '../../../../../../utils'
import { getAvailableVatRatesForReceiver } from './getAvailableVatRatesForReceiver'
import { getCreditNoteOriginalInvoiceGrossTotal } from './getCreditNoteOriginalInvoiceGrossTotal'
import { getDueDateFromSourceDocument } from './getDueDateFromSourceDocument'
import { getLinesValues } from './getLinesValues'
import { getReceiverValues } from './getReceiverValues'

interface GetInvoiceEditorFormValuesFromSourceDocumentOptions {
  getFilteredVatRates?: GetFilteredVatRates
  isCreditNote?: boolean
  isQuote?: boolean
  // When true, keep the source document's own issueDate, number and dueDate instead of
  // inheriting today's date and the next number from defaultInvoiceValues.
  // Used by edit-in-place flows (e.g. QuoteEdit) where the source document IS
  // the target, not a template for a new one.
  preserveSourceIdentity?: boolean
  vatRates?: VatRate[]
}

export const getInvoiceEditorFormValuesFromSourceDocument = (
  sourceDocument: Invoice | Quote,
  defaultInvoiceValues: InvoiceForm,
  {
    getFilteredVatRates,
    isCreditNote,
    isQuote,
    preserveSourceIdentity,
    vatRates,
  }: GetInvoiceEditorFormValuesFromSourceDocumentOptions = {},
): InvoiceForm => {
  const {
    buyerReference,
    dateOfSaleOrService,
    description,
    dueDate,
    footerText,
    language,
    lines,
    paymentMeans,
    paymentTime,
    purchaseOrderNumber,
    summary,
  } = sourceDocument

  const paymentTerm = new NumberValue(paymentTime ?? defaultInvoiceValues.paymentTerm).getValue()
  const resolvedDueDate = getDueDateFromSourceDocument({
    defaultDueDate: defaultInvoiceValues.dueDate,
    issueDate: defaultInvoiceValues.issueDate,
    isQuote,
    paymentTerm,
    preserveSourceIdentity,
    sourceDueDate: dueDate ?? undefined,
  })

  const defaultDateOfSaleOrService = isCreditNote ? null : defaultInvoiceValues.dateOfSaleOrService
  const discountType = sourceDocument.discount?.type ?? defaultInvoiceValues.discountType
  const currency = summary.foreignCurrency || defaultInvoiceValues.currency
  const depositDeduction = 'depositDeduction' in sourceDocument ? sourceDocument.depositDeduction : undefined
  const discount = sourceDocument.discount?.amount ?? 0
  const receiver = getReceiverValues(sourceDocument.customer)
  const invoiceLines = getLinesValues({
    currency,
    defaultLineValues: defaultInvoiceValues.invoiceLines[0],
    discountType,
    getAvailableVatRates: getAvailableVatRatesForReceiver(receiver, getFilteredVatRates),
    isCreditNote,
    lines,
    vatRates,
  })

  const formValues: InvoiceForm = {
    ...defaultInvoiceValues,
    buyerReference: buyerReference || undefined,
    dateOfSaleOrService: dateOfSaleOrService ? new Date(dateOfSaleOrService) : defaultDateOfSaleOrService,
    discount,
    discountType,
    currency,
    dueDate: isCreditNote ? new Date(defaultInvoiceValues.issueDate) : resolvedDueDate,
    // A quote is only ever built from another quote, so this also decides whether the source
    // document's payment methods carry over. Converting a quote into an invoice must not inherit
    // them: the methods announced on a quote are an offer, while the invoice makes its own payment
    // request and starts from the invoice defaults.
    ...(isQuote && {
      displayedPaymentMethods: getDisplayedPaymentMethods(paymentMeans, defaultInvoiceValues.displayedPaymentMethods),
    }),
    exchangeRate: summary?.exchangeRate,
    footerNote: sanitizeStringField(footerText),
    invoiceLines,
    language: invoiceLanguageToLocale[language] || defaultInvoiceValues.language,
    message: sanitizeStringField(description),
    ...(isCreditNote && {
      originalInvoiceTotalIncludingVat: getCreditNoteOriginalInvoiceGrossTotal({
        currency,
        depositDeduction,
        discount,
        discountType,
        invoiceLines,
        summaryGrossTotal: summary.foreignGrossTotal,
      }),
    }),
    paymentTerm,
    purchaseOrderNumber: purchaseOrderNumber || undefined,
    receiver,
  }

  if (preserveSourceIdentity) {
    formValues.issueDate = new Date(sourceDocument.issueDate)

    // Only Quote currently carries an editable source number on the form
    // (estimateNumber → invoiceNumber). Invoice edit-in-place is not a flow today.
    if ('estimateNumber' in sourceDocument && sourceDocument.estimateNumber) {
      const parsedSourceNumber = Number(sourceDocument.estimateNumber)
      if (Number.isFinite(parsedSourceNumber)) {
        formValues.invoiceNumber = parsedSourceNumber
      }
    }
  }

  return formValues
}
